import { isNumber, isString } from 'lodash';
import { Checkbox, NumberInput, TextInput } from '@ardoq/forms';
import { Multiselect, Select } from '@ardoq/select';
import { isTruthy } from '@ardoq/common-helpers';
import {
  getIsAsyncFilterType,
  isNumberPairComparator,
  isWholeNumberFilter,
  QueryBuilderFilterDefinition,
  QueryBuilderFilterType,
} from '../../filterUtils';
import { AsyncLabelLoaders, AsyncSuggestionsLoaders } from '../../types';
import { RuleDatePickerInput } from './RuleDatePickerInput';
import { Flex } from '../atoms';
import { RuleAsyncValueSelect } from './RuleAsyncValueSelect';
import { NumericRangeInput } from './NumericRangeInput';
import { CheckboxIcon } from '@ardoq/icons';
import {
  FilterInfoOperator,
  FormattingOperator,
  Operator,
  QueryBuilderRuleValue,
} from '@ardoq/api-types';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import {
  percentageStoredValueToEditValue,
  percentageEditValueToStoredValue,
  formatNumber,
  Locale,
} from '@ardoq/locale';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { useId } from 'react';

const selectedValues = (
  value?: QueryBuilderRuleValue
): string[] | null | undefined =>
  Array.isArray(value)
    ? value.map(String)
    : value !== undefined && value !== null
      ? [String(value)]
      : value;

const parseToNumber = (value: QueryBuilderRuleValue | undefined) => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }

  return isNumber(value) ? value : parseFloat(value as string);
};

// Number input helpers
const getNumberEditValue = (
  storedValue: QueryBuilderRuleValue | undefined,
  isPercentage: boolean
): number | undefined => {
  const numericValue = parseToNumber(storedValue);
  return isPercentage && numericValue !== undefined
    ? percentageStoredValueToEditValue(numericValue)
    : numericValue;
};

const getNumberDisplayValue = (
  storedValue: QueryBuilderRuleValue | undefined,
  isPercentage: boolean,
  locale: Locale
): string | undefined => {
  const numericValue = parseToNumber(storedValue);
  return isPercentage && numericValue !== undefined
    ? formatNumber(numericValue, 'percentage', locale)
    : undefined;
};

const createNumberValueChangeHandler = (
  isPercentage: boolean,
  onValueChange: (value: QueryBuilderRuleValue) => void
): ((newValue: number | undefined) => void) => {
  return (newValue: number | undefined) => {
    if (newValue === undefined) {
      onValueChange(null);
      return;
    }
    const storedValue = isPercentage
      ? percentageEditValueToStoredValue(newValue)
      : newValue;
    onValueChange(storedValue);
  };
};

// Range input helpers
const getRangeEditValues = (
  value: QueryBuilderRuleValue | undefined,
  isPercentage: boolean
): [number, number] => {
  const [storedFrom, storedTo] = Array.isArray(value)
    ? value.map(Number)
    : [undefined, undefined];

  const editFrom =
    isPercentage && storedFrom !== undefined
      ? percentageStoredValueToEditValue(storedFrom)
      : storedFrom;
  const editTo =
    isPercentage && storedTo !== undefined
      ? percentageStoredValueToEditValue(storedTo)
      : storedTo;

  return [editFrom ?? NaN, editTo ?? NaN];
};

const getRangeDisplayValues = (
  value: QueryBuilderRuleValue | undefined,
  isPercentage: boolean,
  locale: Locale
): { displayValueFrom?: string; displayValueTo?: string } => {
  const [storedFrom, storedTo] = Array.isArray(value)
    ? value.map(Number)
    : [undefined, undefined];

  return {
    displayValueFrom:
      isPercentage && storedFrom !== undefined
        ? formatNumber(storedFrom, 'percentage', locale)
        : undefined,
    displayValueTo:
      isPercentage && storedTo !== undefined
        ? formatNumber(storedTo, 'percentage', locale)
        : undefined,
  };
};

const createRangeValueChangeHandler = (
  isPercentage: boolean,
  onValueChange: (value: QueryBuilderRuleValue) => void
): ((newValue: QueryBuilderRuleValue) => void) => {
  return (newValue: QueryBuilderRuleValue) => {
    if (!Array.isArray(newValue)) {
      onValueChange(newValue);
      return;
    }

    const [newEditFrom, newEditTo] = newValue.map(Number);
    const newStoredFrom =
      isPercentage && !isNaN(newEditFrom)
        ? percentageEditValueToStoredValue(newEditFrom)
        : newEditFrom;
    const newStoredTo =
      isPercentage && !isNaN(newEditTo)
        ? percentageEditValueToStoredValue(newEditTo)
        : newEditTo;

    onValueChange([newStoredFrom, newStoredTo]);
  };
};

type RuleValueInputProps = {
  filterType: QueryBuilderFilterType;
  value?: QueryBuilderRuleValue;
  filter: Omit<QueryBuilderFilterDefinition, 'operator' | 'operators'>; // we reuse same component for formatting, they use comparator and comparators instead of operators
  operator: Operator | FormattingOperator;
  onValueChange: (value: QueryBuilderRuleValue) => void;
  asyncSuggestionsLoaders: AsyncSuggestionsLoaders;
  asyncLabelLoaders: AsyncLabelLoaders;
  isQueryBuilderInReadOnlyMode?: boolean;
  clickId?: string;
  inputId?: string;
  isDisabled?: boolean;
  placeholder?: string;
  hasWarning?: boolean;
  hideWarningIcon?: boolean;
  locale: Locale;
  isNewConditionalFormatting?: boolean;
  menuPortalTarget?: HTMLElement | null;
  renderInPortal?: boolean;
  i18nLocale?: I18nLocale;
};

export const RuleValueInput = ({
  filterType,
  value,
  filter,
  operator,
  onValueChange,
  asyncSuggestionsLoaders,
  asyncLabelLoaders,
  isQueryBuilderInReadOnlyMode = false,
  clickId,
  inputId,
  isDisabled,
  placeholder,
  hasWarning,
  hideWarningIcon,
  locale,
  isNewConditionalFormatting,
  menuPortalTarget,
  renderInPortal,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
}: RuleValueInputProps) => {
  const fallbackInputId = useId();

  const effectiveInputId = inputId || fallbackInputId;

  if (operator === Operator.IS_EMPTY || operator === Operator.IS_NOT_EMPTY) {
    return null;
  }

  if (filterType === QueryBuilderFilterType.STRING) {
    return (
      <Flex flex={1}>
        <TextInput
          isDisabled={isDisabled}
          inputId={effectiveInputId}
          name={filter.label}
          aria-label={t(
            i18nLocale,
            'queryBuilder:ariaLabel.ruleValueText',
            'Rule value text'
          )}
          value={isString(value) ? value : ''}
          onValueChange={onValueChange}
          isReadOnly={isQueryBuilderInReadOnlyMode}
          dataTestId="string-input"
          hasWarning={hasWarning}
          hideWarningIcon={hideWarningIcon}
        />
      </Flex>
    );
  }

  if (
    filterType === QueryBuilderFilterType.DATE ||
    filterType === QueryBuilderFilterType.DATE_TIME ||
    filterType === QueryBuilderFilterType.DATE_RANGE
  ) {
    return isQueryBuilderInReadOnlyMode ? (
      <TextInput
        inputId={effectiveInputId}
        value={isString(value) ? value : ''}
        isDisabled={isDisabled}
      />
    ) : (
      <RuleDatePickerInput
        operator={operator}
        value={value as string}
        filterType={filterType}
        onValueChange={value => onValueChange(value || '')}
        clickId={clickId}
        inputId={effectiveInputId}
        isDisabled={isDisabled}
        hasWarning={hasWarning}
        isNewConditionalFormatting={isNewConditionalFormatting}
        menuPortalTarget={menuPortalTarget}
        renderInPortal={renderInPortal}
        locale={locale}
        i18nLocale={i18nLocale}
      />
    );
  }

  if (filterType === QueryBuilderFilterType.NUMBER) {
    const isPercentage = filter.numberFormatOptions === 'percentage';
    const isWholeNumber = isWholeNumberFilter(filter.id);

    if (isNumberPairComparator(operator)) {
      return (
        <NumericRangeInput
          value={getRangeEditValues(value, isPercentage)}
          onValueChange={createRangeValueChangeHandler(
            isPercentage,
            onValueChange
          )}
          label={filter.label}
          isQueryBuilderInReadOnlyMode={isQueryBuilderInReadOnlyMode}
          inputId={effectiveInputId}
          isDisabled={isDisabled}
          hasWarning={hasWarning}
          hideWarningIcon={hideWarningIcon}
          allowDecimals={!isWholeNumber}
          i18nLocale={i18nLocale}
          {...getRangeDisplayValues(value, isPercentage, locale)}
        />
      );
    }

    return (
      <Flex flex={1}>
        <NumberInput
          name={filter.label}
          aria-label={t(
            i18nLocale,
            'queryBuilder:ariaLabel.ruleValueNumber',
            'Rule value number'
          )}
          isReadOnly={isQueryBuilderInReadOnlyMode}
          allowBlank
          allowDecimals={!isWholeNumber}
          value={getNumberEditValue(value, isPercentage)}
          displayValue={getNumberDisplayValue(value, isPercentage, locale)}
          onValueChange={createNumberValueChangeHandler(
            isPercentage,
            onValueChange
          )}
          dataTestId="number-input"
          inputId={effectiveInputId}
          isDisabled={isDisabled}
          hasWarning={hasWarning}
          hideWarningIcon={hideWarningIcon}
          placeholder={placeholder}
        />
      </Flex>
    );
  }

  if (
    filterType === QueryBuilderFilterType.SELECT ||
    filterType === QueryBuilderFilterType.READONLY
  ) {
    const selectOptionLabel: string | undefined =
      filter.selectOptions?.find(option => option.value === value)?.label ??
      (value as string | undefined);

    return (
      <Flex flex={1}>
        {isQueryBuilderInReadOnlyMode ||
        filterType === QueryBuilderFilterType.READONLY ? (
          <TextInput
            name={filter.label}
            dataTestId="readonly-input"
            inputId={effectiveInputId}
            value={selectOptionLabel}
            isReadOnly
            isDisabled={isDisabled}
            aria-label={t(
              i18nLocale,
              'queryBuilder:ariaLabel.ruleValueText',
              'Rule value text'
            )}
          />
        ) : (
          <Select
            inputId={effectiveInputId}
            name={filter.label}
            aria-label={filter.label}
            options={filter.selectOptions || []}
            value={value}
            onValueChange={value => onValueChange(value as string)}
            clickId={clickId}
            dataTestId="select-input"
            isDisabled={isDisabled}
            menuPortalTarget={menuPortalTarget}
          />
        )}
      </Flex>
    );
  }

  if (filterType === QueryBuilderFilterType.BOOLEAN) {
    if (isQueryBuilderInReadOnlyMode) {
      return (
        <Flex flex={1}>
          <CheckboxIcon checked={value as boolean | null} />
        </Flex>
      );
    }
    if (operator === FilterInfoOperator.BOOLEAN) {
      // boolean custom field formatting rules are displayed with checkbox
      return (
        <Flex flex={1}>
          <Checkbox
            name={filter.label}
            isChecked={value as boolean}
            onChange={value => onValueChange(value)}
            isDisabled={isDisabled}
          >
            <VisuallyHidden>
              {value
                ? t(i18nLocale, 'queryBuilder:boolean.true', 'True')
                : t(i18nLocale, 'queryBuilder:boolean.false', 'False')}
            </VisuallyHidden>
          </Checkbox>
        </Flex>
      );
    }
  }

  if (getIsAsyncFilterType(filterType)) {
    const isAsyncReadonlyFilter =
      filterType === QueryBuilderFilterType.ASYNC_READONLY_REFERENCE_OID;
    return (
      <Flex flex={1}>
        <RuleAsyncValueSelect
          label={filter.label}
          isQueryBuilderInReadOnlyMode={isQueryBuilderInReadOnlyMode}
          isFilterReadonly={isAsyncReadonlyFilter}
          filterType={filterType}
          value={value}
          onValueChange={onValueChange}
          asyncSuggestionsLoaders={asyncSuggestionsLoaders}
          asyncLabelLoaders={asyncLabelLoaders}
          inputId={effectiveInputId}
          isDisabled={isDisabled}
          placeholder={placeholder}
          hasWarning={hasWarning}
          menuPortalTarget={menuPortalTarget}
          i18nLocale={i18nLocale}
        />
      </Flex>
    );
  }

  if (filterType === QueryBuilderFilterType.SELECT_MULTIPLE) {
    const selectedValue = selectedValues(value);
    return (
      <Flex flex={1}>
        <Multiselect
          inputId={effectiveInputId}
          name={filter.label}
          options={filter.selectOptions || []}
          value={selectedValue}
          onValueChange={value => {
            const formattingRuleValue = value?.filter(isTruthy) ?? [];
            onValueChange(formattingRuleValue);
          }}
          clickId={clickId}
          dataTestId="select-input"
          isDisabled={isDisabled}
          placeholder={placeholder}
          menuPortalTarget={menuPortalTarget}
        />
      </Flex>
    );
  }

  return null;
};
